import type {
  EndResponse,
  ErrorResponse,
  FilterResponse,
  InitialRequest as SearchInitialRequest,
  OfferResponse,
} from '@luxuryescapes/contract-search'
import config from 'constants/config'
import { unique } from 'lib/array/arrayUtils'
import {
  addSearchSocketListener,
  removeAllSearchSocketListenersAndSafelyDisconnect,
} from 'lib/search/searchSocketListeners'
import { clearLastMessage, emitSocketMessage } from 'lib/search/socketEmitter'
import uuidV4 from 'lib/string/uuidV4Utils'

const STREAM_TIMEOUT_MS = 20_000

export function getExperienceSearchStreamList(params: {
  placeId: string
  region: string
  categories?: Array<string>
  collections?: Array<string>
  luxPlusFeatures?: Array<App.LuxPlusFeatureFilter>
  campaigns?: Array<string>
  sortBy?: Experiences.SortTypes
  from?: string
  to?: string
  /** Max offers to emit. Omit for count-only streams. */
  limit?: number
  /** When true, request filters/total only (minOffersRequired: 0). */
  countOnly?: boolean
}): Promise<{ experienceIds: Array<string>, total?: number }> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Experience search streaming is browser-only'))
  }

  const searchId = uuidV4()
  const eventGroupKey = `experience-search-stream:${searchId}`
  const experienceIds: Array<string> = []
  const countOnly = !!params.countOnly
  const minOffersRequired = countOnly ? 0 : params.limit
  const flexibleDates = params.from && params.to ? `${params.from}:${params.to}` : undefined
  let total: number | undefined

  return new Promise((resolve, reject) => {
    let settled = false

    const settle = (ids: Array<string> | undefined, error?: Error) => {
      if (settled) return
      settled = true
      clearTimeout(timeoutId)
      clearLastMessage(searchId)
      removeAllSearchSocketListenersAndSafelyDisconnect({ eventGroupKey })
      if (error) {
        reject(error)
        return
      }
      resolve({
        experienceIds: unique(ids ?? experienceIds),
        ...(total != null ? { total } : {}),
      })
    }

    const timeoutId = setTimeout(() => {
      settle(undefined, new Error('Experience search stream timed out'))
    }, STREAM_TIMEOUT_MS)

    addSearchSocketListener({
      eventGroupKey,
      event: 'search:response:filters',
      handler: (response: FilterResponse) => {
        if (response.searchId !== searchId) return
        total = response.total
      },
    })
    addSearchSocketListener({
      eventGroupKey,
      event: 'search:response:offer',
      handler: (response: OfferResponse) => {
        if (response.searchId !== searchId || !response.result?.id) return
        experienceIds.push(response.result.id)
      },
    })
    addSearchSocketListener({
      eventGroupKey,
      event: 'search:response:end',
      handler: (response: EndResponse) => {
        if (response.searchId !== searchId) return
        settle(experienceIds)
      },
    })
    addSearchSocketListener({
      eventGroupKey,
      event: 'search:response:error',
      handler: (errorResponse: ErrorResponse) => {
        if ('searchId' in errorResponse && errorResponse.searchId && errorResponse.searchId !== searchId) return
        if (errorResponse.type === 'searchNotFound') {
          settle([])
          return
        }
        settle(undefined, new Error(`Experience search stream failed: ${errorResponse.type}`))
      },
    })
    // Do not settle on socket disconnect — the shared search socket can drop
    // transiently. socketEmitter resends the stored initial request on reconnect;
    // STREAM_TIMEOUT_MS remains the hard failure backstop.

    emitSocketMessage(
      searchId,
      'search:request:initial',
      {
        searchId,
        region: params.region as SearchInitialRequest['region'],
        brand: config.BRAND as SearchInitialRequest['brand'],
        placeIds: params.placeId,
        offerType: 'experience:*',
        occupancy: [],
        ...(params.limit != null ? { limit: params.limit } : {}),
        ...(minOffersRequired != null ? { minOffersRequired } : {}),
        ...(params.sortBy ? { sortBy: params.sortBy } : {}),
        campaigns: params.campaigns?.join(',') || undefined,
        luxPlusExclusive: params.luxPlusFeatures?.join(',') || undefined,
        categories: params.categories?.join(',') || undefined,
        collections: params.collections?.join(',') || undefined,
        ...(flexibleDates ? { flexibleDates } : {}),
      } as SearchInitialRequest,
    )
  })
}
